"use client";

import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * Persistent banner shown whenever the current session is impersonating another
 * user. Renders nothing for normal sessions. Gives the admin a always-visible
 * way to stop and return to their own account.
 */
export default function ImpersonationBanner() {
  const { isImpersonating, impersonationInfo, user, stopImpersonation } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!isImpersonating) return null;

  const targetName =
    [user?.first_name, user?.sur_name].filter(Boolean).join(" ") ||
    user?.username ||
    "user";
  const roleLabel = user?.sf_role?.label;
  const adminName = impersonationInfo?.impersonator_name;

  const handleStop = async () => {
    if (stopping) return;
    try {
      setStopping(true);
      await stopImpersonation();
      // Full reload so the app re-initializes cleanly as the admin again.
      window.location.href = "/dashboard";
    } catch {
      setStopping(false);
    }
  };

  return (
    <div className="sticky top-0 z-[60] w-full bg-amber-500 text-black text-sm px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 shadow">
      <span>
        You are impersonating <strong>{targetName}</strong>
        {roleLabel ? ` (${roleLabel})` : ""}
        {adminName ? ` — signed in as ${adminName}` : ""}.
      </span>
      <button
        type="button"
        onClick={handleStop}
        disabled={stopping}
        className="underline font-semibold hover:no-underline disabled:opacity-60"
      >
        {stopping ? "Stopping…" : "Stop impersonating"}
      </button>
    </div>
  );
}
